import {AfterViewInit, Component, Inject, ViewChild} from '@angular/core';
import {DialogResult, DialogResultType} from '../DialogResult';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Observable} from 'rxjs/internal/Observable';
import {FilterKeys} from '../../api/models/filter-keys';
import {TextSearchValue} from '../../elements/filter-editor/text-search-filter/text-search-filter.component';
import {Company} from '../../api/models/company';
import {CompanyKey} from '../../api/models/company-key';
import {TitleKey} from '../../api/models/title-key';
import {FullTitleSearchComponent} from '../../elements/filter-editor/full-title-search/full-title-search.component';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {EducationInstitution} from '../../api/models/education-institution';
import {EducationInstitutionKey} from '../../api/models/education-institution-key';
import {DodEndPointEx} from '../../api/models/dod-end-point-ex';
import {SourceJobRemoteStatus} from '../../api/models/source-job-remote-status';
import {SourceJobType} from '../../api/models/source-job-type';
import {SourceJobStatus} from '../../api/models/source-job-status';

export class FilterEditorDialogData {
  filterData: string;
  selectedEndPoints?: DodEndPointEx[];
  allEndPoints?: DodEndPointEx[];
  onlyEndPointSpecific?: boolean;
  // When provided, overrides the endpoint-based visibility rules below entirely: only these tab categories show.
  // ADM has no concept of DoD endpoints, so it scopes tabs this way instead (see AdmLayoutController.
  // GetAvailableFilterCategories and adm-layout-dialog.component.ts's editDirectFilter).
  availableFilterKeys?: string[];
}

export class FilterEditorDialogResult {
  filterData: string;
}

@Component({
  selector: 'filter-editor-dialog',
  templateUrl: './filter-editor-dialog.component.html',
  styleUrls: ['./filter-editor-dialog.component.css'],
})
export class FilterEditorDialogComponent implements AfterViewInit {

  @ViewChild(FullTitleSearchComponent, {static: false}) titleSearchComponent: FullTitleSearchComponent;

  public result: FilterEditorDialogResult;
  public filterKeys: FilterKeys;
  public locationType: string = 'CleanLocation';

  public companySelectedValues: TextSearchValue<Company> = {
    searchTerms: [],
    selectedValues: []
  }

  public educationInstitutionSelectedValues: TextSearchValue<EducationInstitution> = {
    searchTerms: [],
    selectedValues: []
  }

  public titleValues: TitleKey[] = [];
  public postingsWithCompensation: string = 'any';

  public static readonly MaxTrailingMonths = 24;
  public static readonly DefaultTrailingMonths = 3;
  public trailingMonthOptions: number[] = Array.from({length: FilterEditorDialogComponent.MaxTrailingMonths}, (_, i) => i + 1);

  public urgent: string = 'any';
  public sponsored: string = 'any';
  public applyOnIndeed: string = 'any';

  public primaryJobType: number = -1;
  public jobStatus: number = -1;
  public remoteStatus: number = -1;

  public jobPostingDataSource: string = 'indeed';

  public getAffectedEndPoints(filterKey: string): string[] {
    if (!this.data.selectedEndPoints || this.data.selectedEndPoints.length === 0) {
      return [];
    }

    return this.data.selectedEndPoints
      .filter(ep => ep.filters?.some(f => f.key === filterKey))
      .map(ep => ep.title);
  }

  public isTabVisible(filterKey: string): boolean {
    if (this.data.availableFilterKeys) return this.data.availableFilterKeys.includes(filterKey);

    const isEditingPreFilter = this.data.onlyEndPointSpecific === false;
    const hasSelectedEndpoints = this.data.selectedEndPoints && this.data.selectedEndPoints.length > 0;

    const endPointsToQuery = (hasSelectedEndpoints)
      ? this.data.selectedEndPoints
      : this.data.allEndPoints;

    if (!endPointsToQuery || endPointsToQuery.length === 0) {
      return true;
    }

    const supportingEndPoints = endPointsToQuery.filter(ep => ep.filters?.some(f => f.key === filterKey));

    if (supportingEndPoints.length === 0) {
      return false;
    }

    if (this.data.onlyEndPointSpecific === true) {
      const isEndPointSpecific = supportingEndPoints.some(ep => ep.filters?.some(f => f.key === filterKey && f.endPointSpecific));
      if (!isEndPointSpecific) {
        return false;
      }
    } else if (isEditingPreFilter) {
      // For pre-filters, only show non-end point specific filters
      const hasNonEndPointSpecific = supportingEndPoints.some(ep => ep.filters?.some(f => f.key === filterKey && !f.endPointSpecific));
      if (!hasNonEndPointSpecific) {
        return false;
      }
    } else if (!hasSelectedEndpoints) {
      // In DoD context with NO endpoints selected, we should probably hide endpoint-specific tabs
      const isEndPointSpecific = supportingEndPoints.some(ep => ep.filters?.some(f => f.key === filterKey && f.endPointSpecific));
      if (isEndPointSpecific) {
        return false;
      }
    }

    return true;
  }

  constructor(
    public dialogRef: MatDialogRef<FilterEditorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FilterEditorDialogData
  ) {

    if (data.filterData && data.filterData.length > 0) {
      this.filterKeys = JSON.parse(data.filterData);
      // Backfills every field a bare "{}" (e.g. a freshly-created layout's
      // default directFilterData) or an older filter JSON predating one of these fields would be
      // missing - without this, reads/writes below like filterKeys.companies.filter(...) or
      // filterKeys.flags.useCleanLocations = ... throw on undefined.
      this.filterKeys.titles ??= [];
      this.filterKeys.occupations ??= [];
      this.filterKeys.companies ??= [];
      this.filterKeys.industries ??= [];
      this.filterKeys.locations ??= [];
      this.filterKeys.metroGroups ??= [];
      this.filterKeys.attributes ??= [];
      this.filterKeys.educationInstitutions ??= [];
      if (!this.filterKeys.jobPosting) {
        this.filterKeys.jobPosting = {
          postingsWithCompensation: null,
          compensationRateMinimum: null,
          compensationRateMaximum: null,
          jobPostingSource: null
        };
      }
      if (!this.filterKeys.sourceJobPosting) {
        this.filterKeys.sourceJobPosting = {
          primaryJobType: null,
          jobStatus: null,
          minMidSalary: null,
          maxMidSalary: null,
          urgent: null,
          sponsored: null,
          applyOnIndeed: null,
          remoteStatus: null
        }
      }
      if (!this.filterKeys.flags) {
        this.filterKeys.flags = {
          useCleanLocations: "true",
          useMetroGroups: "false"
        };
      }
      // A saved value above the max (or missing while Trailing Months is selected) is clamped so the dropdown always shows a valid choice.
      if (this.filterKeys.dateRange === 7) {
        const saved = this.filterKeys.trailingMonths;
        this.filterKeys.trailingMonths = saved == null || saved < 1
          ? FilterEditorDialogComponent.DefaultTrailingMonths
          : Math.min(saved, FilterEditorDialogComponent.MaxTrailingMonths);
      }
      this.filterKeys.startDate = FilterEditorDialogComponent.fromCalendarDate(this.filterKeys.startDate);
      this.filterKeys.endDate = FilterEditorDialogComponent.fromCalendarDate(this.filterKeys.endDate);
    } else {
      this.filterKeys = {
        titles: [],
        occupations: [],
        companies: [],
        industries: [],
        locations: [],
        metroGroups: [],
        attributes: [],
        educationInstitutions: [],
        jobPosting: {
          postingsWithCompensation: null,
          compensationRateMinimum: null,
          compensationRateMaximum: null,
          jobPostingSource: null
        },
        sourceJobPosting: {
          primaryJobType: null,
          jobStatus: null,
          minMidSalary: null,
          maxMidSalary: null,
          urgent: null,
          sponsored: null,
          applyOnIndeed: null,
          remoteStatus: null
        },
        dateRangeType: 1,
        dateRange: 2,
        flags: {
          useCleanLocations: "true",
          useMetroGroups: "false"
        }
      }
    }

    this.result = {
      filterData: JSON.stringify(this.filterKeys)
    }

    this.companySelectedValues = {
      searchTerms: this.filterKeys.companies.filter(x => x.search).map(x => x.search),
      selectedValues: this.filterKeys.companies.filter(x => x.key).map<Company>(x => {
        return {
          key: x.key,
          name: x.displayName
        }
      })
    };

    this.educationInstitutionSelectedValues = {
      searchTerms: this.filterKeys.educationInstitutions?.filter(x => x.educationInstitutionSearch).map(x => x.educationInstitutionSearch) ?? [],
      selectedValues: this.filterKeys.educationInstitutions?.filter(x => x.key).map<EducationInstitution>(x => {
        return {
          key: x.key,
          institution: x.displayName,
          countryIso: ''
        }
      }) ?? []
    };

    this.locationType = 'CleanLocation';
    if (this.filterKeys.flags?.useMetroGroups === "true") {
      this.locationType = 'MetroGroup';
    }

    if (this.filterKeys.jobPosting?.postingsWithCompensation === true) {
      this.postingsWithCompensation = 'yes';
    } else if (this.filterKeys.jobPosting?.postingsWithCompensation === false) {
      this.postingsWithCompensation = 'no';
    } else {
      this.postingsWithCompensation = 'any';
    }

    if (this.filterKeys.jobPosting?.jobPostingSource === 1) {
      this.jobPostingDataSource = 'indeed';
    } else if (this.filterKeys.jobPosting?.jobPostingSource === 2) {
      this.jobPostingDataSource = 'direct';
    } else {
      this.jobPostingDataSource = 'indeed';
    }

    if (this.filterKeys.sourceJobPosting?.urgent === true) {
      this.urgent = 'yes';
    } else if (this.filterKeys.sourceJobPosting?.urgent === false) {
      this.urgent = 'no';
    } else {
      this.urgent = 'any';
    }

    if (this.filterKeys.sourceJobPosting?.sponsored === true) {
      this.sponsored = 'yes';
    } else if (this.filterKeys.sourceJobPosting?.sponsored === false) {
      this.sponsored = 'no';
    } else {
      this.sponsored = 'any';
    }

    if (this.filterKeys.sourceJobPosting?.applyOnIndeed === true) {
      this.applyOnIndeed = 'yes';
    } else if (this.filterKeys.sourceJobPosting?.applyOnIndeed === false) {
      this.applyOnIndeed = 'no';
    } else {
      this.applyOnIndeed = 'any';
    }

    if (this.filterKeys.sourceJobPosting) {
      if (this.filterKeys.sourceJobPosting.primaryJobType === undefined) {
        this.primaryJobType = -1;
      } else {
        this.primaryJobType = this.filterKeys.sourceJobPosting.primaryJobType;
      }

      if (!this.filterKeys.sourceJobPosting.jobStatus === undefined) {
        this.jobStatus = -1;
      } else {
        this.jobStatus = this.filterKeys.sourceJobPosting.jobStatus;
      }

      if (!this.filterKeys.sourceJobPosting.remoteStatus === undefined) {
        this.remoteStatus = -1;
      } else {
        this.remoteStatus = this.filterKeys.sourceJobPosting.remoteStatus;
      }
    }

    this.Result = new DialogResult();
    this.Result.ResultType = DialogResultType.Cancel;
    this.Result.ResultData = null;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.titleValues = this.filterKeys.titles;
      this.titleSearchComponent.forceRefresh();
    })
  }

  onTabChange($event: MatTabChangeEvent) {
    if ($event.tab.textLabel === 'Titles') {
      this.titleSearchComponent.forceRefresh();
    }
  }

  public Result: DialogResult;

  static showDialog(
    dialog: MatDialog,
    filterData: string,
    selectedEndPoints?: DodEndPointEx[],
    onlyEndPointSpecific?: boolean,
    allEndPoints?: DodEndPointEx[],
    availableFilterKeys?: string[]
  ): Observable<DialogResult> {
    const params: FilterEditorDialogData = {
      filterData,
      selectedEndPoints,
      onlyEndPointSpecific,
      allEndPoints,
      availableFilterKeys
    };

    const dialogRef = dialog.open(FilterEditorDialogComponent, {
      disableClose: true,
      width: '900px',
      data: params,

    });

    return dialogRef.afterClosed();
  }

  // Fixed dates are calendar days, not instants. The datepicker yields a Date at local midnight,
  // which JSON.stringify would convert to UTC - shifting the day back by one for anyone east of UTC.
  // Persist them as plain yyyy-MM-dd instead.
  private static toCalendarDate(value: any): string {
    if (!(value instanceof Date) || isNaN(value.getTime())) {
      return value;
    }
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }

  // new Date('yyyy-MM-dd') parses as UTC midnight, which would show the previous day west of UTC,
  // so build the Date from its parts in local time.
  private static fromCalendarDate(value: any): any {
    const match = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
    if (!match) {
      return value;
    }
    return new Date(+match[1], +match[2] - 1, +match[3]);
  }

  onDateRangeChange(): void {
    if (this.filterKeys.dateRange === 7) {
      this.filterKeys.trailingMonths ??= FilterEditorDialogComponent.DefaultTrailingMonths;
    } else {
      this.filterKeys.trailingMonths = undefined;
    }
  }

  onCancelClick(): void {
    this.dialogRef.close(this.Result);
  }

  onSaveLayout(): void {
    this.closeDialog();
  }

  closeDialog(): void {
    this.filterKeys.companies = [];
    this.filterKeys.companies =
      [
        ...this.companySelectedValues.searchTerms.map<CompanyKey>(x => {return {search: x}}),
        ...this.companySelectedValues.selectedValues.map<CompanyKey>(x => {return {key: x.key, displayName: x.name}})
      ];

    this.filterKeys.educationInstitutions = [];
    this.filterKeys.educationInstitutions =
      [
        ...this.educationInstitutionSelectedValues.searchTerms.map<EducationInstitutionKey>(x => {return {educationInstitutionSearch: x}}),
        ...this.educationInstitutionSelectedValues.selectedValues.map<EducationInstitutionKey>(x => {return {key: x.key, displayName: x.institution}})
      ];

    this.filterKeys.titles = this.titleValues;

    this.filterKeys.flags.useCleanLocations = (this.locationType === 'CleanLocation').toString();
    this.filterKeys.flags.useMetroGroups = (this.locationType === 'MetroGroup').toString();

    if (this.postingsWithCompensation === 'yes') {
      this.filterKeys.jobPosting.postingsWithCompensation = true;
    } else if (this.postingsWithCompensation === 'no') {
      this.filterKeys.jobPosting.postingsWithCompensation = false;
    } else {
      this.filterKeys.jobPosting.postingsWithCompensation = null;
    }

    if (this.jobPostingDataSource === 'indeed') {
      this.filterKeys.jobPosting.jobPostingSource = 1;
    } else if (this.jobPostingDataSource === 'direct') {
      this.filterKeys.jobPosting.jobPostingSource = 2;
    } else {
      this.filterKeys.jobPosting.jobPostingSource = 1;
    }

    if (this.urgent === 'yes') {
      this.filterKeys.sourceJobPosting.urgent = true;
    } else if (this.urgent === 'no') {
      this.filterKeys.sourceJobPosting.urgent = false;
    } else {
      this.filterKeys.sourceJobPosting.urgent = null;
    }

    if (this.sponsored === 'yes') {
      this.filterKeys.sourceJobPosting.sponsored = true;
    } else if (this.sponsored === 'no') {
      this.filterKeys.sourceJobPosting.sponsored = false;
    } else {
      this.filterKeys.sourceJobPosting.sponsored = null;
    }

    if (this.applyOnIndeed === 'yes') {
      this.filterKeys.sourceJobPosting.applyOnIndeed = true;
    } else if (this.applyOnIndeed === 'no') {
      this.filterKeys.sourceJobPosting.applyOnIndeed = false;
    } else {
      this.filterKeys.sourceJobPosting.applyOnIndeed = null;
    }

    if (this.primaryJobType === -1) {
      this.filterKeys.sourceJobPosting.primaryJobType = undefined
    } else {
      this.filterKeys.sourceJobPosting.primaryJobType = this.primaryJobType as SourceJobType
    }
    if (this.jobStatus === -1) {
      this.filterKeys.sourceJobPosting.jobStatus = undefined
    } else {
      this.filterKeys.sourceJobPosting.jobStatus = this.jobStatus as SourceJobStatus
    }
    if (this.remoteStatus === -1) {
      this.filterKeys.sourceJobPosting.remoteStatus = undefined
    } else {
      this.filterKeys.sourceJobPosting.remoteStatus = this.remoteStatus as SourceJobRemoteStatus
    }

    this.filterKeys.startDate = FilterEditorDialogComponent.toCalendarDate(this.filterKeys.startDate);
    this.filterKeys.endDate = FilterEditorDialogComponent.toCalendarDate(this.filterKeys.endDate);

    this.result.filterData = JSON.stringify(this.filterKeys);

    this.Result.ResultType = DialogResultType.Ok;
    this.Result.ResultData = this.result;
    this.dialogRef.close(this.Result);
  }
}
